
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"

export const useWorkspaces = () => {
  const { t } = useTranslation()

  return useQuery({
    queryKey: ['workspaces'],
    queryFn: async () => {
      console.log('Fetching workspaces...')
      
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        console.error('No authenticated user found')
        throw new Error('Not authenticated')
      }
      
      console.log('Current user:', user.id)

      // First get user's organizations
      const { data: orgMembers, error: orgError } = await supabase
        .from('organization_members')
        .select('organization_id')
        .eq('user_id', user.id)

      if (orgError) {
        console.error('Error fetching organization memberships:', orgError)
        toast.error(t('common.workspaces.errorFetching'))
        throw orgError
      }

      console.log('User organization memberships:', orgMembers)

      if (!orgMembers.length) {
        console.log('User has no organization memberships')
        return []
      }

      // Get workspaces with their boards for these organizations
      const { data: workspaces, error: workspacesError } = await supabase
        .from('workspaces')
        .select(`
          id,
          name,
          organization_id,
          organizations (
            id,
            name,
            color
          ),
          boards (
            id,
            name,
            background_url,
            is_starred,
            last_viewed_at
          )
        `)
        .in('organization_id', orgMembers.map(m => m.organization_id))
        .is('archived_at', null)
        .order('name')

      if (workspacesError) {
        console.error('Error fetching workspaces:', workspacesError)
        toast.error(t('common.workspaces.errorFetching'))
        throw workspacesError
      }

      console.log('Successfully fetched workspaces:', workspaces)
      return workspaces || []
    },
    meta: {
      onError: (error: Error) => {
        console.error('Error in workspaces query:', error)
        toast.error(t('common.workspaces.errorFetching'))
      }
    }
  })
}
